import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { supabase as externalSupabase } from '@/integrations/supabase/external-client';
import { useCustomerAuth } from '@/hooks/useCustomerAuth';
import { ensureAdminAlertsChannel } from '@/lib/pushChannel';
import { setCachedFcmToken } from '@/lib/customerFcm';

/**
 * Customer push registration — mirrors the Driver App pattern exactly.
 *
 * On every `registration` event from Capacitor PushNotifications we:
 *   1. Mark all prior `push_tokens` rows for this (customer, platform) as
 *      inactive (so old devices stop receiving pushes immediately).
 *   2. Upsert the new token row with `is_active: true`, keyed on
 *      (user_type, user_id, token) to dedupe replays of the same token.
 *
 * The login-time atomic write still happens server-side in
 * `customer-login.verify_otp` (upsert-before-delete). This client-side
 * hook is the OS-rotation safety net AND the cold-boot fallback for any
 * device where Verify OTP did not have a fresh token at hand.
 */
export function useCustomerPushRegistration() {
  const { customer } = useCustomerAuth();

  useEffect(() => {
    const customerId = customer?.id;
    if (!customerId || !Capacitor.isNativePlatform()) return;

    let removeReg: (() => void) | undefined;
    let removeErr: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      await ensureAdminAlertsChannel();

      const perm = await PushNotifications.checkPermissions();
      let status = perm.receive;
      if (status === 'prompt' || status === 'prompt-with-rationale') {
        status = (await PushNotifications.requestPermissions()).receive;
      }
      if (status !== 'granted') return;

      // Best-effort LocalNotifications permission for foreground banners.
      try {
        const lp = await LocalNotifications.checkPermissions();
        if (lp.display === 'prompt' || lp.display === 'prompt-with-rationale') {
          await LocalNotifications.requestPermissions();
        }
      } catch { /* ignore */ }

      if (cancelled) return;
      await PushNotifications.register();

      const reg = await PushNotifications.addListener('registration', async ({ value: token }) => {
        if (!token) return;
        setCachedFcmToken(token);
        const platform = Capacitor.getPlatform();

        // 1) Mark all prior rows for this customer/platform inactive.
        const { error: deactivateErr } = await externalSupabase
          .from('push_tokens')
          .update({ is_active: false })
          .eq('user_type', 'customer')
          .eq('user_id', customerId)
          .eq('platform', platform);
        if (deactivateErr) {
          console.warn('[push] customer token deactivate failed', deactivateErr);
        }

        // 2) Upsert the new token row.
        const { error } = await externalSupabase.from('push_tokens').upsert(
          {
            user_type: 'customer',
            user_id: customerId,
            platform,
            token,
            is_active: true,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_type,user_id,token' },
        );
        if (error) {
          console.error('[push] customer token upsert failed', error);
        } else {
          console.log('[push] customer token registered');
        }
      });
      removeReg = () => reg.remove();

      const err = await PushNotifications.addListener('registrationError', (e) =>
        console.error('[push] customer registration error', e),
      );
      removeErr = () => err.remove();
    })();

    return () => {
      cancelled = true;
      removeReg?.();
      removeErr?.();
    };
  }, [customer?.id]);
}
